/**
 * Iframe Injector
 *
 * Injects and manages the widget iframe + floating launcher button.
 * The launcher button is rendered in the host page DOM.
 * The iframe is sandboxed with minimal permissions.
 */

export interface IframeHandle {
  iframe: HTMLIFrameElement;
  launcher: HTMLButtonElement;
  show: () => void;
  hide: () => void;
  isOpen: () => boolean;
  postMessage: (msg: unknown) => void;
  showApprovalToolbar: (adminBase: string, widgetId: string, accessToken: string) => void;
}

const LAUNCHER_ID = "__ethos-widget-launcher__";
const IFRAME_ID = "__ethos-widget-iframe__";
const CLOSE_ID = "__ethos-widget-close__";
const APPROVAL_ID = "__ethos-widget-approval__";

/**
 * Inject the launcher button and sandboxed iframe into the host page.
 * Idempotent — returns the existing handle if already injected.
 */
export function injectWidget(
  iframeSrc: string,
  launcherIconUrl: string | undefined,
  primaryColor: string,
  borderRadius?: string,
): IframeHandle {
  const existing = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
  if (existing) {
    const existingLauncher = document.getElementById(LAUNCHER_ID) as HTMLButtonElement;
    return buildHandle(existing, existingLauncher);
  }

  const launcher = buildLauncher(launcherIconUrl, primaryColor);
  const iframe = buildIframe(iframeSrc, borderRadius);

  document.body.appendChild(launcher);
  document.body.appendChild(iframe);

  return buildHandle(iframe, launcher);
}

function buildLauncher(iconUrl: string | undefined, primaryColor: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.id = LAUNCHER_ID;
  btn.setAttribute("aria-label", "Open chat widget");
  btn.style.cssText = [
    "position:fixed",
    "bottom:24px",
    "right:24px",
    "z-index:2147483647",
    "width:56px",
    "height:56px",
    "border-radius:50%",
    `background:${primaryColor}`,
    "border:none",
    "cursor:pointer",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "box-shadow:0 4px 12px rgba(0,0,0,0.2)",
  ].join(";");

  if (iconUrl) {
    const img = document.createElement("img");
    img.src = iconUrl;
    img.alt = "";
    img.style.cssText = "width:28px;height:28px;pointer-events:none";
    btn.appendChild(img);
  } else {
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#fff" viewBox="0 0 16 16" style="pointer-events:none"><path d="M2 1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h9.586a2 2 0 0 1 1.414.586l2 2V2a1 1 0 0 0-1-1zm12-1a2 2 0 0 1 2 2v12.793a.5.5 0 0 1-.854.353l-2.853-2.853a1 1 0 0 0-.707-.293H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z"/><path d="M5 6a1 1 0 1 1-2 0 1 1 0 0 1 2 0m4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/></svg>';
  }

  return btn;
}

function buildIframe(src: string, borderRadius?: string): HTMLIFrameElement {
  const iframe = document.createElement("iframe");
  iframe.id = IFRAME_ID;
  iframe.src = src;
  iframe.setAttribute(
    "sandbox",
    "allow-scripts allow-same-origin allow-forms allow-popups allow-modals",
  );
  // allow-modals helps iOS Safari permit focus on inputs inside sandboxed iframes.
  iframe.setAttribute("allow", "");
  const radius = borderRadius || "12px";
  iframe.style.cssText = [
    "position:fixed",
    "bottom:24px",
    "right:24px",
    "z-index:2147483646",
    "width:380px",
    "height:600px",
    "border:none",
    `border-radius:${radius}`,
    "box-shadow:0 8px 32px rgba(0,0,0,0.2)",
    "display:none",
  ].join(";");
  return iframe;
}

/**
 * Build the approval toolbar for in-situ preview approval.
 *
 * Uses the widget session accessToken (canPreview=true) as Bearer auth.
 * No cookies are sent — this avoids EasyAuth CSRF protection (SubStatusCode 60)
 * and is unaffected by third-party cookie phaseout. ETHOS-1074.
 */
function buildApprovalToolbar(adminBase: string, widgetId: string, accessToken: string): HTMLDivElement {
  const toolbar = document.createElement("div");
  toolbar.id = APPROVAL_ID;
  toolbar.style.cssText = [
    "position:fixed",
    "bottom:700px",
    "right:24px",
    "z-index:2147483646",
    "width:380px",
    "display:none",
    "background:#fff",
    "border-radius:12px",
    "box-shadow:0 8px 32px rgba(0,0,0,0.2)",
    "overflow:hidden",
    "font-family:system-ui,sans-serif",
    "font-size:13px",
  ].join(";");

  const header = document.createElement("div");
  header.style.cssText = "background:#0078d4;color:#fff;padding:10px 14px;font-weight:600;font-size:13px";
  header.textContent = "Preview Approval";
  toolbar.appendChild(header);

  const body = document.createElement("div");
  body.style.cssText = "padding:12px;display:flex;flex-direction:column;gap:8px";

  const label = document.createElement("div");
  label.style.cssText = "color:#555;font-size:12px;line-height:1.4";
  label.textContent = "Review the widget below, then approve or reject.";
  body.appendChild(label);

  const btnRow = document.createElement("div");
  btnRow.style.cssText = "display:flex;gap:8px";

  const approveBtn = document.createElement("button");
  approveBtn.textContent = "Approve";
  approveBtn.style.cssText =
    "flex:1;padding:8px;border-radius:6px;border:none;background:#107c10;color:#fff;cursor:pointer;font-size:13px;font-weight:600";
  approveBtn.addEventListener("click", async () => {
    approveBtn.disabled = true;
    rejectBtn.disabled = true;
    approveBtn.textContent = "Approving...";
    try {
      const bearer = { "X-Widget-Preview-Token": accessToken };

      // Step 1: Fetch current widget (for etag).
      const widgetRes = await fetch(`${adminBase}/api/widgets/${widgetId}`, {
        headers: bearer,
      });
      if (widgetRes.status === 401 || widgetRes.status === 403) {
        throw new Error("Not authorised. The preview session may have expired.");
      }
      if (!widgetRes.ok) throw new Error("Failed to fetch widget");
      const widget = await widgetRes.json();
      let etag = widget._etag ?? "*";

      // Step 2: Record preview attestation (required before approval).
      const attestRes = await fetch(`${adminBase}/api/widgets/${widgetId}/preview-attest`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "If-Match": etag, ...bearer },
        body: JSON.stringify({
          verifiedOrigin: window.location.origin,
          verifiedAssetVersion: widget.assetVersion ?? "latest",
        }),
      });
      if (attestRes.status === 401 || attestRes.status === 403) {
        throw new Error("Not authorised. The preview session may have expired.");
      }
      if (!attestRes.ok) {
        const b = await attestRes.json().catch(() => ({}));
        throw new Error(b.error ?? "Attestation failed");
      }
      const attested = await attestRes.json();
      etag = attested._etag ?? etag;

      // Step 3: Approve (may partially fail if blob storage is unavailable,
      // but the widget status change in Cosmos still succeeds).
      const approveRes = await fetch(`${adminBase}/api/widgets/${widgetId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "If-Match": etag, ...bearer },
        body: JSON.stringify({}),
      });
      if (!approveRes.ok) {
        // Check if approval actually succeeded despite the error (partial failure).
        const recheckRes = await fetch(`${adminBase}/api/widgets/${widgetId}`, { headers: bearer });
        const rechecked = recheckRes.ok ? await recheckRes.json() : null;
        if (rechecked?.status !== "active") {
          const b = await approveRes.json().catch(() => ({}));
          throw new Error(b.error ?? "Approval failed");
        }
      }
      approveBtn.textContent = "Approved";
      approveBtn.style.opacity = "0.6";
      label.textContent = "Widget approved and activated. Reload the page to see it live.";
    } catch (err) {
      approveBtn.textContent = "Approve";
      approveBtn.disabled = false;
      rejectBtn.disabled = false;
      label.textContent = err instanceof Error ? err.message : "Approval failed";
      label.style.color = "#d13438";
    }
  });

  const rejectBtn = document.createElement("button");
  rejectBtn.textContent = "Reject";
  rejectBtn.style.cssText =
    "flex:1;padding:8px;border-radius:6px;border:1px solid #d13438;background:#fff;color:#d13438;cursor:pointer;font-size:13px;font-weight:600";
  rejectBtn.addEventListener("click", () => {
    // Show rejection reason input
    btnRow.style.display = "none";
    rejectForm.style.display = "flex";
  });

  btnRow.appendChild(approveBtn);
  btnRow.appendChild(rejectBtn);
  body.appendChild(btnRow);

  // Rejection reason form (hidden by default)
  const rejectForm = document.createElement("div");
  rejectForm.style.cssText = "display:none;flex-direction:column;gap:8px";

  const textarea = document.createElement("textarea");
  textarea.placeholder = "Rejection reason (required)";
  textarea.rows = 2;
  textarea.style.cssText = "resize:none;border:1px solid #d0d0d0;border-radius:6px;padding:8px;font-size:13px;font-family:inherit";

  const rejectFormBtns = document.createElement("div");
  rejectFormBtns.style.cssText = "display:flex;gap:8px";

  const confirmRejectBtn = document.createElement("button");
  confirmRejectBtn.textContent = "Confirm Reject";
  confirmRejectBtn.style.cssText =
    "flex:1;padding:8px;border-radius:6px;border:none;background:#d13438;color:#fff;cursor:pointer;font-size:13px;font-weight:600";
  confirmRejectBtn.addEventListener("click", async () => {
    const reason = textarea.value.trim();
    if (!reason) return;
    confirmRejectBtn.disabled = true;
    cancelBtn.disabled = true;
    confirmRejectBtn.textContent = "Rejecting...";
    try {
      const bearer = { "X-Widget-Preview-Token": accessToken };

      const widgetRes = await fetch(`${adminBase}/api/widgets/${widgetId}`, { headers: bearer });
      if (widgetRes.status === 401 || widgetRes.status === 403) {
        throw new Error("Not authorised. The preview session may have expired.");
      }
      if (!widgetRes.ok) throw new Error("Failed to fetch widget");
      const widget = await widgetRes.json();
      const etag = widget._etag ?? "*";

      const rejectRes = await fetch(`${adminBase}/api/widgets/${widgetId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "If-Match": etag, ...bearer },
        body: JSON.stringify({ reason }),
      });
      if (rejectRes.status === 401 || rejectRes.status === 403) {
        throw new Error("Not authorised. The preview session may have expired.");
      }
      if (!rejectRes.ok) {
        const b = await rejectRes.json().catch(() => ({}));
        throw new Error(b.error ?? "Rejection failed");
      }
      confirmRejectBtn.textContent = "Rejected";
      confirmRejectBtn.style.opacity = "0.6";
      label.textContent = "Widget rejected and returned to draft.";
    } catch (err) {
      confirmRejectBtn.textContent = "Confirm Reject";
      confirmRejectBtn.disabled = false;
      cancelBtn.disabled = false;
      label.textContent = err instanceof Error ? err.message : "Rejection failed";
      label.style.color = "#d13438";
    }
  });

  const cancelBtn = document.createElement("button");
  cancelBtn.textContent = "Cancel";
  cancelBtn.style.cssText =
    "padding:8px 16px;border-radius:6px;border:1px solid #ccc;background:#fff;cursor:pointer;font-size:13px";
  cancelBtn.addEventListener("click", () => {
    rejectForm.style.display = "none";
    btnRow.style.display = "flex";
    textarea.value = "";
  });

  rejectFormBtns.appendChild(confirmRejectBtn);
  rejectFormBtns.appendChild(cancelBtn);
  rejectForm.appendChild(textarea);
  rejectForm.appendChild(rejectFormBtns);
  body.appendChild(rejectForm);

  toolbar.appendChild(body);
  return toolbar;
}

function buildHandle(iframe: HTMLIFrameElement, launcher: HTMLButtonElement): IframeHandle {
  let open = false;
  let approvalToolbar: HTMLDivElement | null = null;

  // Close button — sits in the upper-right corner of the iframe.
  // Idempotent: reuse existing element on repeated injectWidget calls.
  let closeBtn = document.getElementById(CLOSE_ID) as HTMLButtonElement | null;
  if (!closeBtn) {
    closeBtn = document.createElement("button");
    closeBtn.id = CLOSE_ID;
    closeBtn.setAttribute("aria-label", "Close chat widget");
    closeBtn.textContent = "✕";
    closeBtn.style.cssText = [
      "position:fixed",
      "bottom:588px",  // 24px (iframe bottom) + 600px (height) - 28px (btn) - 8px (inset)
      "right:32px",    // 24px (iframe right) + 8px (inset)
      "z-index:2147483647",
      "width:28px",
      "height:28px",
      "background:rgba(0,0,0,0.45)",
      "color:#fff",
      "border:none",
      "cursor:pointer",
      "display:none",
      "align-items:center",
      "justify-content:center",
      "font-size:14px",
      "line-height:1",
    ].join(";");
    document.body.appendChild(closeBtn);
  }
  const closeBtnEl = closeBtn;

  function doShow() {
    iframe.style.display = "block";
    if (approvalToolbar) approvalToolbar.style.display = "block";
    closeBtnEl.style.display = "flex";
    launcher.style.display = "none";
    launcher.setAttribute("aria-expanded", "true");
    open = true;
  }
  function doHide() {
    iframe.style.display = "none";
    if (approvalToolbar) approvalToolbar.style.display = "none";
    closeBtnEl.style.display = "none";
    launcher.style.display = "flex";
    launcher.setAttribute("aria-expanded", "false");
    open = false;
  }
  closeBtnEl.addEventListener("click", doHide);

  return {
    iframe,
    launcher,
    show: doShow,
    hide: doHide,
    isOpen() {
      return open;
    },
    postMessage(msg: unknown) {
      iframe.contentWindow?.postMessage(msg, "*");
    },
    showApprovalToolbar(adminBase: string, wId: string, accessToken: string) {
      if (approvalToolbar) return;
      approvalToolbar = buildApprovalToolbar(adminBase, wId, accessToken);
      document.body.appendChild(approvalToolbar);
      if (open) approvalToolbar.style.display = "block";
    },
  };
}
